import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Copy, ExternalLink, Store, Download, RefreshCw, Save,
  ShoppingBag, IndianRupee, Crown, Loader2, AlertTriangle,
  Tag, RotateCcw,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

import { useAuth } from "@/context/AuthContext";

// Removed the free-text add form — printers are auto-detected via connector.

// Note: this page belongs to the currently-authenticated retailer only. Every
// piece of state is rebuilt from scratch whenever the auth user changes so a
// logout + login cycle can NEVER show another retailer's QR / URL / name
// (iter 30 P0 multi-tenant bug fix).

export default function MyShop() {
  const { user } = useAuth();
  const uid = user?.id || null;    // capture stable id — resets everything else
  const { t } = useI18n();
  const nav = useNavigate();
  const [shop, setShop] = useState(null);
  const [form, setForm] = useState({ shop_name: "", shop_slug: "", accept_walkins: true });
  const [saving, setSaving] = useState(false);
  const [qrBlobUrl, setQrBlobUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(true);
  const [qrError, setQrError] = useState(null);
  const [walkins, setWalkins] = useState([]);
  const [pricing, setPricing] = useState(null);       // { custom, effective, defaults, ... }
  const [pricingForm, setPricingForm] = useState({}); // strings the user is typing
  const [pricingSaving, setPricingSaving] = useState(false);

  const load = async () => {
    const res = await api.get("/retailer/shop");
    // Defence-in-depth: verify the response header owns the currently
    // authenticated user before we render it. If the API somehow returned
    // another retailer's shop (proxy cache, misrouted request, etc.), we
    // refuse to display it — that's exactly the P0 bug we're guarding.
    const ownerId = res.headers?.["x-maaprint-owner-id"];
    if (ownerId && uid && ownerId !== uid) {
      // eslint-disable-next-line no-console
      console.warn("Tenant leak detected: response owner", ownerId, "expected", uid);
      toast.error("Session mismatch — please sign in again");
      throw new Error("tenant_mismatch");
    }
    setShop(res.data);
    setForm({
      shop_name: res.data.shop_name || "",
      shop_slug: res.data.shop_slug || "",
      accept_walkins: res.data.accept_walkins !== false,
    });
  };

  const loadPricing = async () => {
    try {
      const { data } = await api.get("/retailer/pricing");
      setPricing(data);
      // Seed the form with the retailer's CURRENT effective prices so they
      // never see a blank field — an empty field would look like "free".
      setPricingForm({
        price_bw: String(data.effective.price_bw),
        price_color: String(data.effective.price_color),
        price_duplex_extra: String(data.effective.price_duplex_extra),
        price_passport_sheet: String(data.effective.price_passport_sheet),
        price_id_card: String(data.effective.price_id_card),
      });
    } catch (e) {
      /* non-blocking — retailer can still see the rest of the page */
    }
  };

  const loadWalkins = async () => {
    try {
      const { data } = await api.get("/orders?type=walkin");
      setWalkins(data);
    } catch { /* not blocking */ }
  };

  // Auth-aware QR fetch — the endpoint requires a Bearer token which <img> can't send.
  const loadQr = async () => {
    setQrLoading(true);
    setQrError(null);
    try {
      const res = await api.get("/retailer/shop/qr.png", { responseType: "blob" });
      // Same tenant-verification as /retailer/shop: refuse to render a QR
      // that doesn't own the currently authenticated user (P0 fix).
      const ownerId = res.headers?.["x-maaprint-owner-id"];
      if (ownerId && uid && ownerId !== uid) {
        // eslint-disable-next-line no-console
        console.warn("Tenant leak detected on QR: owner", ownerId, "expected", uid);
        setQrError("Session mismatch — please sign in again");
        return;
      }
      const url = URL.createObjectURL(res.data);
      setQrBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      setQrError(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load QR");
    } finally {
      setQrLoading(false);
    }
  };

  // Reload every piece of shop state whenever the auth user changes AND
  // whenever the browser fires `pageshow` (which covers bfcache restore
  // after browser back/forward). This is the frontend half of the P0
  // tenant-isolation fix.
  useEffect(() => {
    // Force-blank current state so the previous retailer's shop can't flash
    // on screen while the new fetch is in flight.
    setShop(null);
    setQrBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    setWalkins([]);
    setPricing(null);
    setPricingForm({});
    if (!uid) return;   // logged-out — nothing to fetch

    load().catch(() => {});
    loadWalkins();
    loadQr();
    loadPricing();

    const onPageShow = (e) => {
      // `persisted=true` = restored from bfcache. Always refetch — the DOM
      // that was frozen belongs to whoever was signed in earlier.
      if (e.persisted) {
        setShop(null);
        setQrBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
        load().catch(() => {});
        loadWalkins();
        loadQr();
        loadPricing();
      }
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("pageshow", onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/retailer/shop", form);
      setShop(data);
      setForm({
        shop_name: data.shop_name || "",
        shop_slug: data.shop_slug || "",
        accept_walkins: data.accept_walkins !== false,
      });
      await loadQr(); // slug may have changed → refetch QR
      toast.success("Shop updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    if (!shop) return;
    navigator.clipboard.writeText(shop.public_url).then(() => toast.success("Link copied"));
  };

  // --- Custom pricing handlers ------------------------------------------------
  const PRICING_FIELDS = [
    { key: "price_bw",             label: "Black & White (₹ / page)",  hint: "Charged per printed page for B&W documents." },
    { key: "price_color",          label: "Color print (₹ / page)",     hint: "Per-page price when the customer picks color." },
    { key: "price_duplex_extra",   label: "Duplex extra (₹ / page)",    hint: "Added on top of B&W or Color when printing both sides." },
    { key: "price_passport_sheet", label: "Passport sheet (₹ / sheet)", hint: "Flat price for a 4×6\" glossy 8-photo sheet." },
    { key: "price_id_card",        label: "ID Card (₹ / card)",          hint: "Flat price per generated ID card." },
  ];

  const updPrice = (k) => (e) => {
    setPricingForm((p) => ({ ...p, [k]: e.target.value }));
  };

  const savePricing = async () => {
    setPricingSaving(true);
    try {
      // Send every field — validated float on the server. Empty → 0.
      const payload = {};
      for (const f of PRICING_FIELDS) {
        const n = parseFloat(pricingForm[f.key]);
        payload[f.key] = Number.isFinite(n) && n >= 0 ? n : 0;
      }
      const { data } = await api.put("/retailer/pricing", payload);
      setPricing(data);
      toast.success("Your shop prices are live — every walk-in customer will now pay these rates.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPricingSaving(false);
    }
  };

  const resetPricing = async () => {
    setPricingSaving(true);
    try {
      const { data } = await api.put("/retailer/pricing", {});
      setPricing(data);
      setPricingForm({
        price_bw: String(data.effective.price_bw),
        price_color: String(data.effective.price_color),
        price_duplex_extra: String(data.effective.price_duplex_extra),
        price_passport_sheet: String(data.effective.price_passport_sheet),
        price_id_card: String(data.effective.price_id_card),
      });
      toast.success("Reverted to platform default prices.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setPricingSaving(false);
    }
  };

  const downloadQR = async () => {
    if (!qrBlobUrl) return toast.error("QR not ready yet");
    const a = document.createElement("a");
    a.href = qrBlobUrl;
    a.download = `maaprint-${shop.shop_slug}-qr.png`;
    a.click();
  };

  const [regenerating, setRegenerating] = useState(false);
  const regenerateQR = async () => {
    const confirmed = window.confirm(
      "Generate a new QR code?\n\n" +
      "Your CURRENT QR will keep working (customers who already scanned it are safe).\n\n" +
      "Click OK to render a fresh copy at the same URL, or Cancel to keep the current one.\n\n" +
      "Tip: if the QR was lost / leaked and you want to invalidate it entirely, edit the Shop URL under Shop details."
    );
    if (!confirmed) return;
    setRegenerating(true);
    try {
      await api.post("/retailer/shop/qr/regenerate", { rotate_slug: false });
      await loadQr();
      toast.success("QR regenerated. Print & stick the new copy at your counter.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRegenerating(false);
    }
  };

  if (!shop) return null;

  const money = (v) => "₹" + Number(v || 0).toFixed(2);
  const totalEarnings = walkins.reduce((s, o) => s + (o.cost || 0), 0);

  const subActive = shop.subscription_active;
  const shopOnline = subActive && shop.accept_walkins;
  const statusLabel = !subActive ? "Offline (no subscription)" :
                      !shop.accept_walkins ? "Paused" :
                      shop.printers_online > 0 ? "Online" : "Online (no printer)";
  const statusClass = shopOnline
    ? (shop.printers_online > 0
        ? "bg-brand-green/10 text-brand-green border-brand-green/20"
        : "bg-amber-500/10 text-amber-600 border-amber-500/20")
    : "bg-destructive/10 text-destructive border-destructive/20";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="my-shop-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-muted-foreground">Walk-in customers</p>
        <h1 className="text-3xl font-display font-semibold tracking-tight mt-1">My Shop</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Print this QR code and stick it at your counter. Customers scan → upload PDF/JPG → pay → their print appears in your queue and prints automatically.
        </p>
      </div>

      {!subActive && (
        <Card className="p-4 border-amber-500/40 bg-amber-500/5 flex items-center gap-3" data-testid="myshop-sub-banner">
          <AlertTriangle size={18} className="text-amber-600" />
          <div className="flex-1 text-sm">
            <b>Your shop is offline for walk-ins.</b> Activate a subscription to accept customer QR uploads.
          </div>
          <Button size="sm" className="bg-brand-green hover:bg-brand-green-hover"
            onClick={() => nav("/subscription")} data-testid="myshop-activate-sub-btn">
            <Crown size={13} className="mr-1.5" /> Activate
          </Button>
        </Card>
      )}

      <div className="grid lg:grid-cols-5 gap-6">
        {/* QR */}
        <Card className="lg:col-span-2 p-6 border-border bg-card text-center">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Your shop QR</p>
            <Badge variant="outline" className={statusClass} data-testid="shop-status-badge">
              {statusLabel}
            </Badge>
          </div>
          <div className="bg-white rounded-xl border border-border p-4 inline-flex items-center justify-center min-h-[15rem] min-w-[15rem]" data-testid="shop-qr-image">
            {qrLoading ? (
              <Loader2 className="animate-spin text-muted-foreground" size={32} />
            ) : qrError ? (
              <div className="text-center px-4">
                <p className="text-xs text-destructive mb-2">{qrError}</p>
                <Button variant="outline" size="sm" onClick={loadQr} data-testid="shop-qr-retry-btn">
                  <RefreshCw size={13} className="mr-1" /> Retry
                </Button>
              </div>
            ) : qrBlobUrl ? (
              <img
                src={qrBlobUrl}
                alt={`QR code for ${shop.shop_name}`}
                className="w-56 h-56 object-contain"
                data-testid="shop-qr-img"
              />
            ) : null}
          </div>
          <p className="mt-4 text-lg font-display font-semibold">{shop.shop_name}</p>
          <p className="text-xs text-muted-foreground font-mono mt-1 break-all">{shop.public_url}</p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={copyLink} data-testid="shop-copy-link-btn">
              <Copy size={14} className="mr-1" /> Copy link
            </Button>
            <Button variant="outline" onClick={downloadQR} disabled={!qrBlobUrl} data-testid="shop-download-qr-btn">
              <Download size={14} className="mr-1" /> Download QR
            </Button>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={regenerateQR}
            disabled={regenerating || !qrBlobUrl}
            className="mt-2 text-xs text-muted-foreground hover:text-foreground"
            data-testid="shop-regenerate-qr-btn"
          >
            <RefreshCw size={12} className={`mr-1 ${regenerating ? "animate-spin" : ""}`} />
            {regenerating ? "Regenerating…" : "Regenerate QR (manual)"}
          </Button>
          <p className="mt-1 text-[10px] text-muted-foreground max-w-[16rem] mx-auto leading-snug">
            Your QR is permanent — it never changes on deploys, logins, connector installs or PC swaps. Only this button (or renaming your Shop URL) creates a new one.
          </p>
          <a
            href={shop.public_url}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-xs text-primary hover:underline"
            data-testid="shop-open-public-btn"
          >
            Open public page <ExternalLink size={12} />
          </a>
        </Card>

        {/* Settings + earnings */}
        <div className="lg:col-span-3 space-y-6">
          <Card className="p-6 border-border bg-card">
            <div className="flex items-center gap-2 mb-4">
              <Store size={18} className="text-primary" />
              <h3 className="font-display font-semibold text-lg">Shop details</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Display name</Label>
                <Input
                  value={form.shop_name}
                  onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
                  className="mt-1.5"
                  data-testid="shop-name-input"
                />
              </div>
              <div className="sm:col-span-2">
                <Label>Shop URL slug</Label>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{BACKEND_URL}/shop/</span>
                  <Input
                    value={form.shop_slug}
                    onChange={(e) => setForm({ ...form, shop_slug: e.target.value })}
                    className="font-mono"
                    data-testid="shop-slug-input"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Lowercase letters, digits and dashes. 3-40 chars.</p>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border sm:col-span-2">
                <div>
                  <Label>Accept walk-in orders</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Turn off temporarily if you&apos;re closed.</p>
                </div>
                <Switch
                  checked={form.accept_walkins}
                  onCheckedChange={(v) => setForm({ ...form, accept_walkins: v })}
                  data-testid="shop-accept-switch"
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <Button onClick={save} disabled={saving} className="bg-primary hover:bg-brand-blue-hover" data-testid="shop-save-btn">
                <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </Card>

          <Card className="p-6 border-border bg-card" data-testid="retailer-pricing-card">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Tag size={18} className="text-brand-green" />
                <h3 className="font-display font-semibold text-lg">Your shop pricing</h3>
              </div>
              {pricing && (
                <span className="text-[11px] text-muted-foreground">
                  Platform fee: <b className="tabular-nums">{pricing.platform_commission_pct}%</b> · GST: <b className="tabular-nums">{pricing.gst_pct}%</b>
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mb-4">
              Every customer who scans your QR pays <b>these prices</b>. On successful payment the amount is credited to your wallet after the platform commission is deducted automatically.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {PRICING_FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="flex items-center justify-between">
                    <span>{f.label}</span>
                    {pricing && pricing.custom[f.key] == null && (
                      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Default</span>
                    )}
                  </Label>
                  <div className="mt-1.5 relative">
                    <IndianRupee size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      value={pricingForm[f.key] ?? ""}
                      onChange={updPrice(f.key)}
                      className="pl-8 tabular-nums"
                      data-testid={`retailer-pricing-${f.key}`}
                    />
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">{f.hint}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-between mt-5 gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={resetPricing}
                disabled={pricingSaving || !pricing}
                className="text-muted-foreground hover:text-foreground"
                data-testid="retailer-pricing-reset-btn"
              >
                <RotateCcw size={13} className="mr-1.5" /> Reset to platform defaults
              </Button>
              <Button
                onClick={savePricing}
                disabled={pricingSaving || !pricing}
                className="bg-brand-green hover:bg-brand-green-hover text-white"
                data-testid="retailer-pricing-save-btn"
              >
                <Save size={14} className="mr-2" /> {pricingSaving ? "Saving…" : "Save pricing"}
              </Button>
            </div>
          </Card>

          <Card className="p-6 border-border bg-card">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShoppingBag size={18} className="text-brand-green" />
                <h3 className="font-display font-semibold text-lg">Walk-in earnings</h3>
              </div>
              <Button variant="ghost" size="icon" onClick={loadWalkins} data-testid="walkins-refresh-btn">
                <RefreshCw size={14} />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 rounded-lg border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-widest">Walk-in orders</p>
                <p className="text-3xl font-display font-semibold tabular-nums mt-2">{walkins.length}</p>
              </div>
              <div className="p-4 rounded-lg border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-widest">Total earnings</p>
                <p className="text-3xl font-display font-semibold tabular-nums text-brand-green mt-2">{money(totalEarnings)}</p>
              </div>
            </div>
            <div className="mt-4 divide-y divide-border max-h-72 overflow-y-auto">
              {walkins.length === 0 ? (
                <p className="text-sm text-muted-foreground py-6 text-center">No walk-in orders yet. Share your QR!</p>
              ) : walkins.slice(0, 10).map((o) => (
                <div key={o.id} className="py-3 flex items-center gap-3" data-testid={`walkin-row-${o.id}`}>
                  <div className="h-9 w-9 rounded-lg bg-brand-green/10 text-brand-green flex items-center justify-center flex-shrink-0">
                    <IndianRupee size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{o.customer_name || "Guest"} · {o.filename}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(o.created_at).toLocaleString()} · {o.status}
                    </p>
                  </div>
                  <span className="font-medium tabular-nums text-brand-green">{money(o.cost)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
